import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import BookDemoModal from '../../Components/BookDemoModal/BookDemoModal';
import { Calendar, Mail, ArrowRight, Sparkles, Layers, Cpu, Globe, ExternalLink } from 'lucide-react';

const CALENDLY_URL = "https://calendly.com/sanjaysoni-provaantech/vendor-empanelment-discussion-provaantech";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const BookDemoPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);

  useEffect(() => {
    const handleCalendlyEvent = async (e) => {
      if (e.origin === "https://calendly.com" && e.data && e.data.event === "calendly.event_scheduled") {
        console.log("Calendly event scheduled:", e.data.payload);
        const payload = e.data.payload || {};
        const invitee = payload.invitee || {};
        const event = payload.event || {};

        const name = invitee.name || (invitee.first_name ? `${invitee.first_name} ${invitee.last_name || ''}`.trim() : 'Calendly Booking');
        const email = invitee.email || 'N/A';
        const date = event.start_time || new Date().toISOString();
        const phone = invitee.text_reminder_number || invitee.phone || 'Calendly Sync';
        const message = `Scheduled directly via Calendly Inline Page`;

        try {
          const res = await fetch(`${API_BASE_URL}/api/v1/demo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name,
              email,
              phone,
              message,
              date,
              turnstileToken: 'calendly-verified-token'
            })
          });

          if (res.ok) {
            toast.success('Calendly booking synced to Admin Dashboard!');
          }
        } catch (err) {
          console.error('Failed to sync Calendly event to backend:', err);
        }
      }
    };

    window.addEventListener('message', handleCalendlyEvent);
    return () => window.removeEventListener('message', handleCalendlyEvent);
  }, []);

  const embedUrl = `${CALENDLY_URL}?embed_domain=${encodeURIComponent(window.location.hostname || 'localhost')}&embed_type=Inline&primary_color=3b82f6`;

  return (
    <div style={{ minHeight: '90vh', background: 'radial-gradient(ellipse at top, #1e293b 0%, #0f172a 100%)', color: '#f8fafc', padding: '90px 20px 100px', fontFamily: '"Inter", system-ui, sans-serif', position: 'relative', overflow: 'hidden' }}>
      
      {/* Dynamic Background Glow */}
      <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '350px', background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, rgba(168, 85, 247, 0.08) 50%, transparent 70%)', filter: 'blur(70px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: '1160px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        
        {/* Header Hero */}
        <div style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto 64px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 20px', background: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(59, 130, 246, 0.3)', color: '#60a5fa', borderRadius: '100px', fontSize: '0.875rem', fontWeight: 600, marginBottom: '24px' }}>
            <Sparkles size={16} /> Enterprise Digital Transformation & AI Solutions
          </div>

          <h1 style={{ fontSize: '3.5rem', fontWeight: 800, lineHeight: 1.12, marginBottom: '24px', letterSpacing: '-0.03em', color: '#ffffff' }}>
            Book a <br />
            <span style={{ background: 'linear-gradient(135deg, #60a5fa 0%, #c084fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Discovery Call
            </span>
          </h1>

          <p style={{ fontSize: '1.2rem', color: '#94a3b8', lineHeight: 1.6, margin: '0 auto' }}>
            Book a 1-on-1 demonstration with our solution architects. Discover how ToxBox AI, ProHirey, GCC Acceleration, and Cloud Engineering drive operational dominance for your enterprise.
          </p>
        </div>

        {/* Embedded Calendly Section */}
        <div style={{ background: '#ffffff', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)', marginBottom: '56px', position: 'relative', minHeight: '760px' }}>
          <style>{`
            @keyframes spin-loader {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <div style={{ padding: '20px 28px', background: '#0f172a', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Calendar size={22} color="#60a5fa" /> Direct Live Scheduling
              </h3>
              <span style={{ fontSize: '0.875rem', color: '#94a3b8' }}>Select your preferred discussion slot below</span>
            </div>
            <a 
              href={CALENDLY_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)', color: '#60a5fa', borderRadius: '100px', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}
            >
              Open Calendly <ExternalLink size={14} />
            </a>
          </div>
          
          <div style={{ position: 'relative', minHeight: '680px', background: '#ffffff' }}>
            {isIframeLoading && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', zIndex: 10 }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '4px solid rgba(59, 130, 246, 0.15)', borderTopColor: '#3b82f6', animation: 'spin-loader 1s linear infinite' }} />
                <div style={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 500, fontFamily: '"Inter", system-ui, sans-serif' }}>Loading Live Scheduler...</div>
              </div>
            )}
            <iframe
              src={embedUrl}
              onLoad={() => setIsIframeLoading(false)}
              width="100%"
              height="700px"
              frameBorder="0"
              title="Provaantech Calendly Scheduler"
              style={{ border: 'none', minHeight: '680px', width: '100%', display: isIframeLoading ? 'none' : 'block' }}
            />
          </div>
        </div>

        {/* Platforms in Production Section */}
        <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '56px', marginBottom: '32px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 800, color: '#ffffff', marginBottom: '12px', letterSpacing: '-0.02em' }}>Production Platforms</h2>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: '600px', margin: '0 auto' }}>
            Explore our enterprise-grade platforms currently deployed and active in the market.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '28px', marginBottom: '64px' }}>
          {/* ToxBox AI */}
          <div 
            onClick={() => window.open('https://toxboxai.com/', '_blank')}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.borderColor = '#38bdf8'; e.currentTarget.style.boxShadow = '0 15px 30px -10px rgba(56, 189, 248, 0.25)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: 'rgba(255, 255, 255, 0.03)', padding: '28px', borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.08)', cursor: 'pointer', transition: 'all 0.3s ease' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(56, 189, 248, 0.12)', color: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Cpu size={24} />
              </div>
              <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                ToxBox AI <ExternalLink size={14} style={{ opacity: 0.6 }} />
              </div>
            </div>
            <div style={{ fontSize: '0.95rem', color: '#94a3b8', lineHeight: 1.5 }}>
              AI-driven toxicity detection, content safety moderation, and custom LLM alignment engines.
            </div>
          </div>

          {/* ProHirey */}
          <div 
            onClick={() => window.open('https://prohirey.com/', '_blank')}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.borderColor = '#c084fc'; e.currentTarget.style.boxShadow = '0 15px 30px -10px rgba(192, 132, 252, 0.25)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: 'rgba(255, 255, 255, 0.03)', padding: '28px', borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.08)', cursor: 'pointer', transition: 'all 0.3s ease' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(192, 132, 252, 0.12)', color: '#c084fc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Layers size={24} />
              </div>
              <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                ProHirey <ExternalLink size={14} style={{ opacity: 0.6 }} />
              </div>
            </div>
            <div style={{ fontSize: '0.95rem', color: '#94a3b8', lineHeight: 1.5 }}>
              A robust matching hub where clients, vendors, and candidates seamlessly connect.
            </div>
          </div>

          {/* GCC Acceleration */}
          <div 
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.borderColor = '#34d399'; e.currentTarget.style.boxShadow = '0 15px 30px -10px rgba(52, 211, 153, 0.25)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: 'rgba(255, 255, 255, 0.03)', padding: '28px', borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.08)', transition: 'all 0.3s ease' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(52, 211, 153, 0.12)', color: '#34d399', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Globe size={24} />
              </div>
              <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.15rem' }}>
                GCC Acceleration
              </div>
            </div>
            <div style={{ fontSize: '0.95rem', color: '#94a3b8', lineHeight: 1.5 }}>
              Tailored strategies for global center expansion, offshore scaling, and key leadership team setup.
            </div>
          </div>
        </div>

        {/* Centered Email Solutions Card (Horizontal Layout) */}
        <div style={{ maxWidth: '960px', margin: '0 auto 64px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '56px' }}>
          <div 
            style={{ 
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.7) 100%)', 
              borderRadius: '24px', 
              padding: '32px 40px', 
              border: '1px solid rgba(255, 255, 255, 0.1)', 
              backdropFilter: 'blur(20px)', 
              display: 'flex', 
              flexDirection: 'row', 
              flexWrap: 'wrap', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              gap: '28px', 
              cursor: 'pointer', 
              transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)', 
              boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.5)' 
            }}
            onClick={() => window.location.href = 'mailto:sales@provaantech.com?subject=Enterprise Discovery & Solutions Inquiry'}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.borderColor = '#c084fc'; e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(168, 85, 247, 0.2)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)'; e.currentTarget.style.boxShadow = '0 20px 40px -15px rgba(0, 0, 0, 0.5)'; }}
          >
            {/* Left Box: Icon & Details */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: '1 1 500px' }}>
              <div style={{ width: '56px', height: '56px', minWidth: '56px', borderRadius: '16px', background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(147, 51, 234, 0.3) 100%)', color: '#c084fc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Mail size={26} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#ffffff', marginBottom: '6px', letterSpacing: '-0.01em' }}>Email Solutions Team</h3>
                <p style={{ color: '#94a3b8', lineHeight: 1.5, fontSize: '0.95rem', margin: 0 }}>
                  Need RFPs, custom enterprise architecture reviews, or specialized compliance consultations? Reach out directly to our principal engineers.
                </p>
              </div>
            </div>

            {/* Right Box: Action Info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '250px', flex: '1 1 auto', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', color: '#c084fc', fontWeight: 700, fontSize: '1.1rem', gap: '8px' }}>
                sales@provaantech.com <ArrowRight size={18} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', color: '#94a3b8' }}>
                <span>Contact Person:</span>
                <span style={{ color: '#ffffff', fontWeight: 600 }}>+91 8431498169</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      <BookDemoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BookDemoPage;
